import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { StoryImportReviewElement } from "@/features/story-import/story-import.types";
import {
  getStoryImportConfidenceKey,
  getStoryImportElementTypeKey,
  getStoryImportReviewStatusKey,
} from "@/features/story-import/storyImportMappers";

interface ImportReviewCardProps {
  element: StoryImportReviewElement;
  isBusy?: boolean;
  onAccept: () => void;
  onDiscard: () => void;
  onEdit: () => void;
}

const confidenceToneClasses = {
  high: "bg-emerald-100 text-emerald-900",
  low: "bg-amber-100 text-amber-950",
  medium: "bg-sky-100 text-sky-950",
} as const;

const statusToneClasses = {
  accepted: "bg-emerald-100 text-emerald-900",
  discarded: "bg-zinc-200 text-zinc-900",
  linked: "bg-sky-100 text-sky-950",
  merged: "bg-violet-100 text-violet-950",
  pending: "bg-amber-100 text-amber-950",
} as const;

export function ImportReviewCard({
  element,
  isBusy = false,
  onAccept,
  onDiscard,
  onEdit,
}: ImportReviewCardProps) {
  const { t } = useTranslation();
  const isRelationshipSuggestion = element.elementType === "relationship";
  const evidencePreview = useMemo(
    () => element.evidence.slice(0, 2),
    [element.evidence],
  );
  const evidenceSummary = useMemo(() => {
    if (element.evidence.length === 0) {
      return null;
    }

    const chapters = [...new Set(element.evidence.flatMap((source) => source.chapterNumbers))]
      .sort((left, right) => left - right);
    const temporalMarker = element.evidence.find((source) => source.temporalMarker)?.temporalMarker ?? null;
    const explicitness = element.evidence.every((source) => source.explicitness === "explicit")
      ? "explicit"
      : "inferred";

    return {
      chapters,
      explicitness,
      temporalMarker,
    };
  }, [element.evidence]);
  const confidenceScoreLabel = element.confidenceDetails?.score ?? element.confidenceScore;
  const evidenceReferencePreview = useMemo(
    () =>
      evidencePreview.map((source) =>
        [
          source.chapterLabel,
          source.paragraphLabel,
          source.sourceRef,
        ]
          .filter((value): value is string => Boolean(value?.trim()))
          .join(" | "),
      ),
    [evidencePreview],
  );

  return (
    <article className="rounded-[1.5rem] border border-ink/10 bg-white/80 p-5 shadow-vellum">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-3 xl:max-w-xl">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">
              {element.proposalTypeLabel ?? t(getStoryImportElementTypeKey(element.elementType))}
            </Badge>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${confidenceToneClasses[element.confidence]}`}
            >
              {t(getStoryImportConfidenceKey(element.confidence))}
            </span>
            {element.needsReview ? (
              <span className="inline-flex rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-950">
                {t("storyImport.review.needsReviewBadge")}
              </span>
            ) : null}
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${statusToneClasses[element.reviewStatus]}`}
            >
              {t(getStoryImportReviewStatusKey(element.reviewStatus))}
            </span>
          </div>

          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground">{element.displayTitle}</h3>
            <p className="text-sm leading-6 text-muted-foreground">{element.previewText}</p>
            {typeof confidenceScoreLabel === "number" ? (
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-clay">
                {t("storyImport.review.confidenceScore", {
                  score: Math.round(confidenceScoreLabel * 100),
                })}
              </p>
            ) : null}
          </div>
          {evidenceSummary ? (
            <div className="rounded-[1.25rem] border border-ink/10 bg-canvas/80 px-4 py-3 text-sm text-muted-foreground">
              <p className="font-semibold text-foreground">{t("storyImport.review.evidenceSummary.title")}</p>
              <p className="mt-1">
                {t("storyImport.review.evidenceSummary.body", {
                  chapters: evidenceSummary.chapters.length > 0
                    ? evidenceSummary.chapters.join(", ")
                    : t("storyImport.review.evidenceSummary.noChapter"),
                  explicitness: t(`storyImport.review.evidenceSummary.explicitness.${evidenceSummary.explicitness}`),
                  temporalMarker: evidenceSummary.temporalMarker ?? t("storyImport.review.evidenceSummary.noMarker"),
                })}
              </p>
            </div>
          ) : null}
          {isRelationshipSuggestion ? (
            <div className="rounded-[1.25rem] border border-amber-200/70 bg-amber-50/90 px-4 py-3 text-sm text-amber-950">
              <p className="font-semibold">{t("storyImport.review.relationshipSuggestion.title")}</p>
              <p className="mt-1">{t("storyImport.review.relationshipSuggestion.body")}</p>
            </div>
          ) : null}
          {element.notes.length > 0 ? (
            <div className="rounded-[1.25rem] border border-rose-200/70 bg-rose-50/80 px-4 py-3 text-sm text-rose-950">
              <p className="font-semibold">{t("storyImport.review.notesTitle")}</p>
              <div className="mt-2 space-y-1">
                {element.notes.map((note) => (
                  <p key={`${element.id}-${note}`}>{note}</p>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="min-w-0 flex-1 space-y-3 xl:max-w-2xl">
          {evidencePreview.map((source, index) => (
            <div
              className="rounded-[1.25rem] border border-ink/10 bg-canvas/80 px-4 py-3"
              key={`${element.id}-${index}`}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">
                {t("storyImport.review.evidenceLabel", {
                  count: index + 1,
                })}
              </p>
              <p className="mt-2 text-sm leading-6 text-foreground">{source.excerpt}</p>
              {evidenceReferencePreview[index] ? (
                <p className="mt-2 text-xs font-medium text-clay">
                  {t("storyImport.review.evidenceReference", {
                    reference: evidenceReferencePreview[index],
                  })}
                </p>
              ) : null}
              <p className="mt-2 text-xs text-muted-foreground">{source.reason}</p>
              {source.contradictionFlag ? (
                <p className="mt-2 text-xs font-medium text-rose-900">
                  {t("storyImport.review.contradictionFlag")}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {element.reviewStatus === "pending" && !isRelationshipSuggestion ? (
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button
            disabled={isBusy}
            onClick={onEdit}
            type="button"
            variant="outline"
          >
            {t("storyImport.actions.editBeforeAccept")}
          </Button>
          <Button
            disabled={isBusy}
            onClick={onDiscard}
            type="button"
            variant="ghost"
          >
            {t("storyImport.actions.discard")}
          </Button>
          <Button
            disabled={isBusy}
            onClick={onAccept}
            type="button"
          >
            {t("storyImport.actions.accept")}
          </Button>
        </div>
      ) : null}
    </article>
  );
}
